import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import './OutstandingCallPage.css';

const TeamPage = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(9);
  const [filterText, setFilterText] = useState('');
  const [assignToFilter, setAssignToFilter] = useState(''); // Filter by Assign To
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({
    teamName: '',
    color: '',
    assignTo: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [teamNameError, setTeamNameError] = useState('');
  const [colorError, setColorError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});


  // Fetch data from Team API
  useEffect(() => {
    const fetchTeamData = async () => {
      try {
        const apiUrl = process.env.REACT_APP_API_URL;
        const baseUrl = apiUrl.endsWith('/') ? apiUrl.slice(0, -1) : apiUrl;
        
        const token = localStorage.getItem("token");
        if (!token) {
          toast.error("Please login again");
          return;
        }
        
        const config = { headers: { Authorization: `Bearer ${token}` } };
        const response = await axios.get(`${baseUrl}/api/team`, config);
        
        if (response.data.success) {
          setData(response.data.data);
        } else {
          toast.error("Failed to fetch data");
        }
      } catch (error) {
        console.error("Error fetching Team data:", error);
        toast.error("Failed to fetch data");
      } finally {
        setLoading(false);
      }
    };

    fetchTeamData();
  }, []);

  // Filter data based on search text and Assign To filter
  const filteredData = data.filter(item => {
    // First apply Assign To filter
    if (assignToFilter && item.assignTo && item.assignTo.toLowerCase() !== assignToFilter.toLowerCase()) {
      return false;
    }
    
    // Then apply search text filter
    if (!filterText) return true;
    
    const searchText = filterText.toLowerCase();
    return (
      (item.teamName && item.teamName.toLowerCase().includes(searchText)) ||
      (item.color && item.color.toLowerCase().includes(searchText)) ||
      (item.assignTo && item.assignTo.toLowerCase().includes(searchText))
    );
  });

  // Use filtered data directly (no sorting)
  const sortedData = filteredData;

  // Pagination
  const totalPages = Math.ceil(sortedData.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentData = sortedData.slice(startIndex, endIndex);


  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Clear field error when user starts typing
    if (fieldErrors[name]) {
      setFieldErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }

    // Validate team name in real-time
    if (name === 'teamName') {
      const trimmedValue = value.trim();
      if (trimmedValue) {
        const teamNameExists = data.some(team => 
          team.teamName.toLowerCase() === trimmedValue.toLowerCase()
        );
        if (teamNameExists) {
          setTeamNameError('Team name already exists');
        } else {
          setTeamNameError('');
        }
      } else {
        setTeamNameError('');
      }
    }

    // Validate color in real-time
    if (name === 'color') {
      const trimmedValue = value.trim();
      if (trimmedValue) {
        const colorExists = data.some(team => 
          team.color.toLowerCase() === trimmedValue.toLowerCase()
        );
        if (colorExists) {
          setColorError('Color already exists');
        } else {
          setColorError('');
        }
      } else {
        setColorError('');
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.teamName.trim() || !formData.color || !formData.assignTo) {
      toast.error('Fill all fields');
      return;
    }

    // Check if team name already exists in current data
    const teamNameExists = data.some(team => 
      team.teamName.toLowerCase() === formData.teamName.trim().toLowerCase()
    );
    
    if (teamNameExists) {
      setTeamNameError('Team name already exists');
      toast.error('Team name exists');
      return;
    }

    // Check if color already exists in current data
    const colorExists = data.some(team => 
      team.color.toLowerCase() === formData.color.trim().toLowerCase()
    );
    
    if (colorExists) {
      setColorError('Color already exists');
      toast.error('Color already exists');
      return;
    }

    // Clear previous field errors
    setFieldErrors({});

    setSubmitting(true);
    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      const baseUrl = apiUrl.endsWith('/') ? apiUrl.slice(0, -1) : apiUrl;
      
      const token = localStorage.getItem("token");
      if (!token) {
        toast.error("Please login again");
        return;
      }
      
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const response = await axios.post(`${baseUrl}/api/team`, formData, config);
      
      if (response.data.success) {
        toast.success('Team added successfully');
        setShowModal(false);
        setFormData({ teamName: '', color: '', assignTo: '' });
        setTeamNameError('');
        setColorError('');
        // Refresh data
        const fetchResponse = await axios.get(`${baseUrl}/api/team`, config);
        if (fetchResponse.data.success) {
          setData(fetchResponse.data.data);
        }
      } else {
        toast.error('Failed to add');
      }
    } catch (error) {
      console.error('Error adding team:', error);
      
      // Handle validation errors from backend
      if (error.response && error.response.data) {
        const errorData = error.response.data;
        
        // Handle field-specific errors
        if (errorData.field) {
          const { field, message } = errorData;
          
          if (field === 'color') {
            setColorError(message);
          } else if (field === 'teamName') {
            setTeamNameError(message);
          }
          
          setFieldErrors({ [field]: message });
          
          // Focus on the problematic field instead of showing toast
          setTimeout(() => {
            const fieldElement = document.querySelector(`[name="${field}"]`);
            if (fieldElement) {
              fieldElement.focus();
              fieldElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
          }, 100);
        } else if (errorData.message) {
          // Handle general error messages
          if (errorData.message.includes('Color') || errorData.message.toLowerCase().includes('color')) {
            setColorError(errorData.message);
          } else if (errorData.message.includes('Team name') || errorData.message.toLowerCase().includes('team name')) {
            setTeamNameError(errorData.message);
          }
          toast.error(errorData.message || 'Failed to add');
        } else {
          toast.error('Failed to add');
        }
      } else {
        toast.error('Failed to add');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setFormData({ teamName: '', color: '', assignTo: '' });
    setTeamNameError('');
    setColorError('');
    setFieldErrors({});
  };

  return (
    <div className="outstanding-call-page no-max-height team-page-no-scroll">
      <div className="table-container">
        {/* Control Bar */}
        <div className="control-bar">
        
          
          <div className="control-right">
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ fontSize: '14px', color: '#6c757d', fontWeight: '500' }}>Filter by:</span>
              <select
                value={assignToFilter}
                onChange={(e) => {
                  setAssignToFilter(e.target.value);
                  setCurrentPage(1); // Reset to first page when filter changes
                }}
                style={{
                  padding: '8px 32px 8px 12px',
                  border: '2px solid #007bff',
                  borderRadius: '4px',
                  fontSize: '14px',
                  cursor: 'pointer',
                  backgroundColor: 'white',
                  color: '#333',
                  fontWeight: '500',
                  appearance: 'none',
                  backgroundImage: `url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%23007bff' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6,9 12,15 18,9'%3e%3c/polyline%3e%3c/svg%3e")`,
                  backgroundRepeat: 'no-repeat',
                  backgroundPosition: 'right 8px center',
                  backgroundSize: '16px',
                  minWidth: '160px',
                  boxShadow: assignToFilter ? '0 2px 4px rgba(0, 123, 255, 0.2)' : 'none',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.target.style.borderColor = '#0056b3';
                  e.target.style.boxShadow = '0 2px 6px rgba(0, 123, 255, 0.3)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.borderColor = '#007bff';
                  e.target.style.boxShadow = assignToFilter ? '0 2px 4px rgba(0, 123, 255, 0.2)' : 'none';
                }}
              >
                <option value="">All Assign To</option>
                <option value="southeastern">Southeastern</option>
                <option value="ukpn">UKPN</option>
              </select>
            </div>
            <input
              type="text"
              placeholder="Filter in records..."
              value={filterText}
              onChange={(e) => {
                setFilterText(e.target.value);
                setCurrentPage(1); // Reset to first page when filter changes
              }}
              className="filter-input"
            />
            <button 
              className="new-entry-btn"
              onClick={() => setShowModal(true)}
              title="Add New Team"
            >
              Add
            </button>
          </div>
        </div>

        {/* Data Table */}
        <div className="table-section">
          <div className="table-wrapper" style={{ overflowX: 'hidden' }}>
          <table className="data-table">
            <thead>
              <tr>
                <th>Team Name</th>
                <th>Color</th>
                <th>Assign To</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="3" style={{ textAlign: 'center', padding: '20px' }}>
                    Loading Team data...
                  </td>
                </tr>
              ) : currentData.length === 0 ? (
                <tr>
                  <td colSpan="3" style={{ textAlign: 'center', padding: '20px' }}>
                    No Team data found
                  </td>
                </tr>
              ) : (
                currentData.map((item) => (
                  <tr key={item._id || item.id}>
                    <td>{item.teamName || ''}</td>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <div 
                          style={{ 
                            width: '20px', 
                            height: '20px', 
                            backgroundColor: item.color || '#ccc', 
                            borderRadius: '4px',
                            border: '1px solid #ddd'
                          }}
                        ></div>
                        <span>{item.color || ''}</span>
                      </div>
                    </td>
                    <td>
                      <span style={{ 
                        textTransform: 'uppercase',
                        fontWeight: 'bold',
                        color: item.assignTo === 'southeastern' ? '#8b1634' : '#007bff'
                      }}>
                        {item.assignTo || ''}
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
          </div>

          {/* Pagination */}
          <div className="pagination">
          <button 
            onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
            disabled={currentPage === 1}
            className="pagination-btn"
          >
            Previous
          </button>
          <span className="pagination-info">
            Page {currentPage} of {totalPages}
          </span>
          <button 
            onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
            disabled={currentPage === totalPages}
            className="pagination-btn"
          >
            Next
          </button>
          </div>
        </div>
      </div>

      {/* Add Team Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={handleCloseModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Add New Team</h2>
              <button className="modal-close" onClick={handleCloseModal}>×</button>
            </div>
            
            <form onSubmit={handleSubmit} className="modal-form">
              <div className="form-group">
                <label htmlFor="teamName">Team Name *</label>
                <input
                  type="text"
                  id="teamName"
                  name="teamName"
                  value={formData.teamName}
                  onChange={handleInputChange}
                  placeholder="Enter team name"
                  required
                  style={{ 
                    borderColor: teamNameError ? '#dc3545' : '#ddd',
                    borderWidth: teamNameError ? '2px' : '1px'
                  }}
                />
                {teamNameError && (
                  <div style={{ 
                    color: '#dc3545', 
                    fontSize: '12px', 
                    marginTop: '4px',
                    fontWeight: '500'
                  }}>
                    {teamNameError}
                  </div>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="color">Color *</label>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <input
                    type="color"
                    id="color"
                    name="color"
                    value={formData.color || '#000000'}
                    onChange={handleInputChange}
                    required
                    style={{
                      width: '50px',
                      height: '40px',
                      border: (colorError || fieldErrors.color) ? '2px solid #dc3545' : '1px solid #ddd',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      padding: '0'
                    }}
                  />
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <div 
                      style={{ 
                        width: '20px', 
                        height: '20px', 
                        backgroundColor: formData.color || '#000000', 
                        borderRadius: '4px',
                        border: '1px solid #ddd'
                      }}
                    ></div>
                    <span style={{ fontSize: '14px', color: '#666' }}>
                      {formData.color || '#000000'}
                    </span>
                  </div>
                </div>
                {colorError && (
                  <div style={{ 
                    color: '#dc3545', 
                    fontSize: '0.875rem', 
                    marginTop: '4px',
                    fontWeight: '500'
                  }}>
                    {colorError}
                  </div>
                )}
                {fieldErrors.color && !colorError && (
                  <div style={{ 
                    color: '#dc3545', 
                    fontSize: '0.875rem', 
                    marginTop: '4px',
                    fontWeight: '500'
                  }}>
                    {fieldErrors.color}
                  </div>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="assignTo">Assign To *</label>
                <select
                  id="assignTo"
                  name="assignTo"
                  value={formData.assignTo}
                  onChange={handleInputChange}
                  required
                >
                  <option value="">Select location</option>
                  <option value="southeastern">Southeastern</option>
                  <option value="UKPN">UKPN</option>
                </select>
              </div>

              <div className="modal-actions">
                <button 
                  type="button" 
                  onClick={handleCloseModal}
                  className="btn-cancel"
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  disabled={submitting || teamNameError}
                  className="btn-submit"
                >
                  {submitting ? 'Saving...' : 'Save Team'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default TeamPage;
